import { Amplify } from 'aws-amplify'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './api/query-client'
import Root from './Root'
import { ThemeProvider, CssBaseline } from '@mui/material'
import './i18n/configs'
import { default_theme } from './theme'
import { MeSideEffects } from './hooks/useMe'
import AuthBootGate from './components/AuthBootGate'
import { startAuthListener } from './api/auth-listener'
import { ScrollPositionProvider } from './hooks/scroll-position'
import { ImageUploaderProvider } from './hooks/image-uploader/image-uploader'
import { SeamlessSnackbarProvider } from './contexts/SeamlessSnackbarContext'
import { CacheSharePasswordProvider } from './hooks/cache-share-password'
import { SensitiveContentMemoryProvider } from './contexts/SensitiveContentMemoryContext'
import { MutedContentMemoryProvider } from './contexts/MutedContentMemoryContext'
import { BirthDateDialogProvider } from './contexts/BirthDateDialogContext'
import Maintenance from './pages/Maintenance'
import AutoReloadProvider from './components/AutoReload/AutoReloadProvider'
import useSyncSystemBars from './hooks/useSyncSystemBars'
import HistorySessionGuard from './pages/History/HistorySessionGuard'

const SystemBarsSync: React.FC = () => { useSyncSystemBars(); return null }

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_AWS_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_AWS_USER_POOL_CLIENT_ID as string,
      loginWith: {
        username: false,
        email: true,
      },
    }
  }
})
startAuthListener()

const App: React.FC = () =>
  <ThemeProvider
    theme={default_theme}
    {...(FEATURE_DARK_MODE ? {
      defaultMode: 'system' as const,
      modeStorageKey: 'metamory_color_mode',
      colorSchemeStorageKey: 'metamory_color_scheme',
    } : {})}
  >
    <SystemBarsSync />
    <CssBaseline />
    <title>
      {import.meta.env.VITE_APP_TITLE}
    </title>
    {MAINTENANCE ?
      <Maintenance/>
    :
      <QueryClientProvider client={queryClient}>
        <MeSideEffects/>
        <AuthBootGate>
          <SensitiveContentMemoryProvider>
            <MutedContentMemoryProvider>
              <BirthDateDialogProvider>
                <SeamlessSnackbarProvider>
                  <ScrollPositionProvider>
                    <HistorySessionGuard/>
                    <ImageUploaderProvider>
                      <CacheSharePasswordProvider>
                        <AutoReloadProvider/>
                        <Root/>
                      </CacheSharePasswordProvider>
                    </ImageUploaderProvider>
                  </ScrollPositionProvider>
                </SeamlessSnackbarProvider>
              </BirthDateDialogProvider>
            </MutedContentMemoryProvider>
          </SensitiveContentMemoryProvider>
        </AuthBootGate>
      </QueryClientProvider>
    }
  </ThemeProvider>

export default App
